import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import Alert from '@material-ui/lab/Alert';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, Token } from '@stripe/stripe-js';
import { isEmail } from 'class-validator';
import moment from 'moment';
import amexLogo from 'payment-icons/min/flat/amex.svg';
import defaultLogo from 'payment-icons/min/flat/default.svg';
import discoverLogo from 'payment-icons/min/flat/discover.svg';
import masterCardLogo from 'payment-icons/min/flat/mastercard-old.svg';
import visaLogo from 'payment-icons/min/flat/visa.svg';
import React, { useEffect, useState } from 'react';

import {
  PaymentInformation,
  SubscriptionStatus,
  useGetLoggedInUserQuery,
  useGetPaymentInformationQuery,
  useGetStripePublicKeyQuery,
  useUpdatePaymentInformationMutation,
} from '../../../../graphql/gen/types';
import { colorPalette, TextSizesEnum } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexTextField } from '../../../shared/primitives/PlexTextField';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { SettingsContent } from '../SettingsContent';
import { useAccountStyles } from './styles';

function PaymentInfoRow({
  paymentInfo,
  onClickEdit,
}: {
  paymentInfo: PaymentInformation | null | undefined;
  onClickEdit: () => void;
}): React.ReactElement {
  const classes = useCreditCardStyles();

  const creditCardLogo = ((): string => {
    switch (paymentInfo?.cardBrand) {
      case 'Visa':
        return visaLogo;
      case 'MasterCard':
        return masterCardLogo;
      case 'Discover':
        return discoverLogo;
      case 'American Express':
        return amexLogo;
      default:
        return defaultLogo;
    }
  })();

  return (
    <div className={classes.container}>
      {paymentInfo !== null && (
        <>
          <div className={classes.logoContainer}>
            <img src={creditCardLogo} alt="Visa Logo" height={40} width={60} />
          </div>
          <div className={classes.textContainer}>
            <PlexTypography size={TextSizesEnum.Small} className={classes.textTitleColor}>
              Card ending in
            </PlexTypography>
            <PlexTypography size={TextSizesEnum.Normal}>{paymentInfo?.cardLastFourDigits}</PlexTypography>
          </div>
          <div className={classes.textContainer}>
            <PlexTypography size={TextSizesEnum.Small} className={classes.textTitleColor}>
              Cardholder Name
            </PlexTypography>
            <PlexTypography size={TextSizesEnum.Normal}>{paymentInfo?.cardholderName}</PlexTypography>
          </div>
          <div className={classes.textContainer}>
            <PlexTypography size={TextSizesEnum.Small} className={classes.textTitleColor}>
              Expiry Date
            </PlexTypography>
            <PlexTypography size={TextSizesEnum.Normal}>{paymentInfo?.cardExpiration}</PlexTypography>
          </div>
        </>
      )}
      {!paymentInfo && (
        <PlexTypography size={TextSizesEnum.Normal} className={classes.singleText}>
          No credit card on file for this account
        </PlexTypography>
      )}
      <PlexIconButton MuiIcon={EditIcon} onClick={onClickEdit} color={ButtonColor.Transparent} />
    </div>
  );
}

function BillingContent(): React.ReactElement {
  const classes = useAccountStyles();
  const { data: paymentInformationData, loading: paymentInformationLoading } = useGetPaymentInformationQuery({
    fetchPolicy: 'network-only',
  });
  const { data: currentUserData, loading: currentUserLoading } = useGetLoggedInUserQuery();
  const [
    updatePaymentInformation,
    { loading: updatePaymentInformationLoading },
  ] = useUpdatePaymentInformationMutation();
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [editCreditCard, setEditCreditCard] = useState(false);
  const [cardIsValid, setCardIsValid] = useState(false);
  const [formChanged, setFormChanged] = useState(false);

  const defaultName =
    paymentInformationData?.currentUser?.domain?.paymentInformation?.cardholderName ||
    currentUserData?.currentUser?.name ||
    '';

  const defaultEmail =
    paymentInformationData?.currentUser?.domain?.paymentInformation?.billingEmail ||
    currentUserData?.currentUser?.email ||
    '';

  const defaultAddress =
    paymentInformationData?.currentUser?.domain?.paymentInformation?.billingAddress ||
    currentUserData?.currentUser?.domain?.address ||
    '';

  const subscriptionInformation = paymentInformationData?.currentUser?.domain?.subscriptionInformation;

  const [alertSeverity, alertMessage] = ((): ['error' | 'info', string] => {
    switch (subscriptionInformation?.status) {
      case SubscriptionStatus.Active:
        return ['info', 'active'];
      case SubscriptionStatus.Trialing:
        return ['info', 'trialing'];
      case SubscriptionStatus.Unpaid:
        return ['error', 'unpaid'];
      case SubscriptionStatus.PastDue:
        return ['error', 'past due'];
      default:
        return ['error', 'unknown'];
    }
  })();

  const numSeats = subscriptionInformation?.numberOfSeats || 0;

  useEffect(() => {
    setName(defaultName);
    setEmail(defaultEmail);
    setAddress(defaultAddress);
  }, [defaultName, defaultEmail, defaultAddress]);

  const formLoading = currentUserLoading || paymentInformationLoading;
  const formIsReady =
    !formLoading &&
    formChanged &&
    // Either we are not editing the card on file or the card has to be be valid and the name has to not be empty
    (!editCreditCard || (cardIsValid && name !== '')) &&
    address !== '' &&
    isEmail(email);

  const handleSubmit = async (): Promise<void> => {
    const token = await (async (): Promise<Token | null> => {
      if (editCreditCard && stripe && elements) {
        const card = elements.getElement(CardElement);
        if (card) {
          const { error, token } = await stripe.createToken(card, { name });
          if (error) {
            throw error;
          } else {
            return token || null;
          }
        } else {
          throw new Error('card element is missing while attempting to edit credit card');
        }
      } else {
        return null;
      }
    })();

    const res = await updatePaymentInformation({
      variables: { paymentMethodId: token?.id, billingEmail: email, billingAddress: address },
    });

    if (res.data?.updatePaymentMethod?.paymentInformation) {
      const { cardholderName, billingEmail, billingAddress } = res.data?.updatePaymentMethod?.paymentInformation;
      setName(cardholderName);
      setEmail(billingEmail);
      setAddress(billingAddress);
      setCardIsValid(false);
      setEditCreditCard(false);
      setFormChanged(false);
    }
  };

  const resetForm = (): void => {
    setFormChanged(false);
    setName(defaultName);
    setEmail(defaultEmail);
    setAddress(defaultAddress);
    setCardIsValid(false);
    setEditCreditCard(false);
  };

  if (formLoading) {
    return <div>Loading...</div>;
  } else {
    return (
      <SettingsContent
        title={'Billing'}
        onSave={handleSubmit}
        onCancel={resetForm}
        updateDisabled={!formIsReady}
        loadingMutation={updatePaymentInformationLoading}
      >
        {subscriptionInformation != null && (
          <Alert
            severity={alertSeverity}
            className={classes.billingInfoMessage}
            action={
              <Button
                color="inherit"
                onClick={(): Promise<unknown> =>
                  fetch('/api/stripe/billing-redirect', { method: 'POST' })
                    .then((res) => res.json())
                    .then((resp) => (window.location.href = resp.redirectURL))
                }
              >
                SEE INVOICES
              </Button>
            }
          >
            You are subscribed for {numSeats} plex {numSeats > 1 ? 'seats' : 'seat'}. Your subscription is currently{' '}
            {alertMessage}.
            {subscriptionInformation.status === SubscriptionStatus.Trialing
              ? ` Your trial ends ${moment(subscriptionInformation.trialEnd).fromNow()}.`
              : ''}
            {subscriptionInformation.status === SubscriptionStatus.Active
              ? ` Your current billing period ends ${moment(subscriptionInformation.currentPeriodEnd).fromNow()}.`
              : ''}
          </Alert>
        )}
        <PlexTextField
          id={'billing-email'}
          label={'Billing Email'}
          className={classes.borderRight}
          value={email}
          handleChange={(value): void => {
            setFormChanged(true);
            setEmail(value);
          }}
        />
        <PlexTextField
          id={'billing-address'}
          label={'Billing Address'}
          className={classes.halfWidth}
          value={address}
          handleChange={(value): void => {
            setFormChanged(true);
            setAddress(value);
          }}
        />
        {!editCreditCard && (
          <PaymentInfoRow
            paymentInfo={paymentInformationData?.currentUser?.domain?.paymentInformation}
            onClickEdit={(): void => {
              setFormChanged(true);
              setEditCreditCard(true);
            }}
          />
        )}
        {editCreditCard && (
          <>
            <PlexTextField
              id={'cardholder-name'}
              label={'Cardholder Name'}
              className={classes.borderRight}
              value={name}
              handleChange={(value): void => setName(value)}
            />
            <div className={classes.creditCard}>
              <CardElement
                onChange={(chg): void => {
                  if (chg.complete) {
                    setCardIsValid(true);
                  } else if (chg.empty || chg.error) {
                    setCardIsValid(false);
                  }
                }}
              />
            </div>
          </>
        )}
      </SettingsContent>
    );
  }
}

export const Billing = (): React.ReactElement => {
  const { data, loading } = useGetStripePublicKeyQuery();
  const [stripe, setStripe] = useState<Stripe | null>(null);

  useEffect(() => {
    const stripePK = data?.loggedIn?.stripePublicKey;
    if (stripePK) {
      loadStripe(stripePK).then((s) => setStripe(s));
    }
  }, [data]);

  if (loading || !stripe) {
    return <div>Loading...</div>;
  } else {
    return (
      <Elements stripe={stripe}>
        <BillingContent />
      </Elements>
    );
  }
};

const useCreditCardStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `1px solid ${colorPalette.dark._500}`,
    justifyContent: 'space-between',
  },
  logoContainer: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  textTitleColor: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  singleText: {
    alignSelf: 'center',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));
